import { useMergedRef } from "@mantine/hooks";
import cardStyles from "../../card.module.scss";
import styles from "./card-anomaly.module.scss";
import classNames from "classnames";
import type {
    CardContentItem,
    CustomCardAnomalyFactory,
    ICardAnomalySkeletonProps,
    IProps,
} from "./card-anomaly.types";
import { polymorphicComponentWrapper } from "../../../../utilities";
import { Card } from "../../card-component";
import { Group } from "../../../group";
import { Stack } from "../../../stack";
import { SimpleGrid } from "../../../simple-grid";
import { Text } from "../../../text";
import { Icon } from "../../../icon";
import { Title } from "../../../title";
import { ActionIcon } from "../../../action-icon";
import { Badge } from "../../../badge";
import { Avatar } from "../../../avatar";
import { rem } from "@mantine/core";

const CardAnomaly = polymorphicComponentWrapper<CustomCardAnomalyFactory, IProps>(
    "CardAnomaly",
    ({ content, heading, statusIcon, customStatusIcon, statusText, columns = 1, ...props }, ref) => {
        const mergedRef = useMergedRef(ref);
        const contentItems = content
            .filter((item): item is CardContentItem => Boolean(item))
            .map((item) => (
                <Stack key={item.title} gap="xs">
                    <Text dimmed size="xs" variant="medium">
                        {item.title}
                    </Text>
                    <Title order={5} variant="bold">
                        {item.value}
                    </Title>
                </Stack>
            ));

        return (
            <Card
                ref={mergedRef}
                shadow="0"
                bdrs="lg"
                data-variant={props.variant}
                classNames={{
                    root: classNames(cardStyles["root"], styles["root"]),
                    section: classNames(cardStyles["section"], styles["section"]),
                }}
                {...props}
            >
                <Stack gap="xl">
                    <Group w="100%" justify="space-between" id="header">
                        <Title order={4} c="var(--mantine-color-text-title-2)">
                            {heading}
                        </Title>

                        {statusText ? (
                            <Badge
                                color={props.variant}
                                rightSection={statusIcon ? <Icon icon={statusIcon} /> : customStatusIcon}
                                variant="light"
                                h={rem(34)}
                                pr="0"
                                classNames={{
                                    root: styles["badge"],
                                    section: styles["badge-section"],
                                }}
                            >
                                {statusText}
                            </Badge>
                        ) : (
                            // Matches the badge height so every card's header and content line up.
                            <Avatar
                                size={rem(34)}
                                variant="filled"
                                bg={
                                    props.variant === "error"
                                        ? "var(--surface-error-transparent)"
                                        : "var(--surface-primary-transparent)"
                                }
                            >
                                {statusIcon && <Icon color="var(--mantine-color-neutral-9)" icon={statusIcon} />}
                            </Avatar>
                        )}
                    </Group>
                    {columns > 1 ? (
                        <SimpleGrid cols={columns} spacing="lg" verticalSpacing="md">
                            {contentItems}
                        </SimpleGrid>
                    ) : (
                        <Stack gap="md">{contentItems}</Stack>
                    )}
                </Stack>
            </Card>
        );
    }
);

const CardAnomalySkeleton = (props: ICardAnomalySkeletonProps) => {
    const skeletonColor = "var(--surface-gray-transparent-color)";
    return (
        <Card
            data-color="gray"
            classNames={{
                root: classNames(cardStyles["root"], styles["root"]),
                section: classNames(cardStyles["section"], styles["section"]),
            }}
            {...props}
        >
            <Stack gap="xl">
                <Group>
                    <Stack gap="0" flex={1}>
                        <Text.Skeleton color={skeletonColor} disableDarken size="sm" width={120} />
                    </Stack>
                    <ActionIcon.Skeleton color={skeletonColor} disableDarken bdrs="xl" size="input-xs" />
                </Group>
                <Group id="metrics" gap="xl">
                    <Stack gap="sm">
                        <Group gap="sm">
                            <Title.Skeleton color={skeletonColor} disableDarken order={2} width={40} />
                            <Text.Skeleton color={skeletonColor} disableDarken size="xs" width={50} />
                        </Group>
                        <Text.Skeleton color={skeletonColor} disableDarken size="sm" width={80} />
                    </Stack>
                </Group>
            </Stack>
        </Card>
    );
};

CardAnomaly.Skeleton = CardAnomalySkeleton;

export { CardAnomaly, CardAnomalySkeleton };
